/** One specialist's verdict on a clause, in full: its status, why, on what, who can
 * accept a deviation, and when and in which conversation it was set. */
import { Link } from "react-router-dom";

import type { AgentLineStatus } from "../api";
import { projectSpecialistPath, runLinkPath } from "../routes/learningAgentPaths";
import { formatWhen } from "../utils/formatWhen";
import { Cited } from "./Cited";
import styles from "./Clauses.module.css";
import { STATUS_LABEL, statusText } from "./clauseStatus";
import { WrittenBeforeBranch } from "./AgentSlotBar";

/** A Ready to build check's id, as a verdict a check gave names it ("Met, as rc-...
 * records"): must agree with src/inrange_agents/registers/check_mirror.py. */
const CHECK_ID = /(rc-[0-9a-f]{12})/;

/** The verdicts a check gives the lines it tracks while it is open: a check withdrawn or
 * superseded rewrites them (not applicable, recheck), and the tab shows no closed check,
 * so only these name a check there is somewhere to go to. */
const FROM_OPEN_CHECK: ReadonlySet<string> = new Set(["meets", "not_evidenced"]);

/** The reasoning, each check it names a link to that check where the tab shows it. */
const Reasoning = ({
  text,
  href,
}: {
  text: string;
  href: ((checkId: string) => string) | null;
}) => (
  <>
    {text.split(CHECK_ID).map((part, i) =>
      i % 2 === 1 && href ? (
        <Link key={i} to={href(part)}>
          {part}
        </Link>
      ) : (
        part
      ),
    )}
  </>
);

export const ClauseVerdict = ({
  seen,
  agentLabel,
  projectId,
}: {
  seen: AgentLineStatus;
  agentLabel: (agent: string) => string;
  /** The project the verdict is on: a check it names is on its specialist's tab there. */
  projectId: string;
}) => {
  const a = seen.assessment;
  return (
    <div className={styles.verdict}>
      <div className={styles.verdictHead}>
        <strong>{statusText(seen.status, a)}</strong>
        <span className={styles.muted}>by {agentLabel(seen.agent)}</span>
        {a && a.status !== seen.status && (
          <span className={styles.muted}>
            set as {STATUS_LABEL[a.status]}
            {a.due_at ? ` at ${a.due_at}` : ""}
          </span>
        )}
        {a?.set_at && <span className={styles.muted}>{formatWhen(a.set_at)}</span>}
        {a?.set_by_run_id && (
          <span>
            <Link to={runLinkPath(a.set_by_run_id)}>conversation</Link>
            <WrittenBeforeBranch at={a.set_at} />
          </span>
        )}
      </div>
      {!a ? (
        <p className={styles.muted}>Not assessed yet.</p>
      ) : (
        <>
          <p className={styles.assessment}>
            <span className={styles.muted}>
              {seen.status === "deferred" ? "Deferred because: " : "Reasoning: "}
            </span>
            <Reasoning
              text={a.reason}
              href={
                FROM_OPEN_CHECK.has(a.status)
                  ? (id) => `${projectSpecialistPath(projectId, seen.agent)}#${id}`
                  : null
              }
            />
          </p>
          {a.evidence && (
            <div className={styles.assessment}>
              <span className={styles.muted}>Sources: </span>
              <Cited text={a.evidence} inline />
            </div>
          )}
          {a.accepts_deviation && (
            <p className={styles.assessment}>
              <span className={styles.muted}>Who can accept a deviation: </span>
              {a.accepts_deviation}
            </p>
          )}
        </>
      )}
      {seen.moved.length > 0 && (
        <p className={styles.contested}>
          A document it cites has changed since, so it is due a recheck.
        </p>
      )}
    </div>
  );
};
